import { ShieldCheck, CalendarCheck, Scale, Handshake } from "lucide-react";
import Reveal from "@/components/Reveal";
import { IMGS } from "@/data";
import { useLang } from "@/i18n";

const icons = [ShieldCheck, CalendarCheck, Scale, Handshake];

const Trust = () => {
  const { t } = useLang();
  return (
    <section id="trust" data-testid="trust-section" className="border-t border-ink/10 bg-white py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <Reveal className="flex flex-col justify-between gap-8 md:flex-row md:items-end">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-clay">{t.trust.overline}</p>
            <h2 data-testid="trust-heading" className="mt-6 font-serif text-4xl tracking-tight text-ink sm:text-5xl">
              {t.trust.h2a} <em className="italic text-clay">{t.trust.h2b}</em>
            </h2>
          </div>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 gap-px overflow-hidden border border-ink/10 bg-ink/10 sm:grid-cols-2 lg:grid-cols-4">
          {t.trust.why.map((w, i) => {
            const Icon = icons[i];
            return (
              <Reveal key={w.t} delay={i * 0.08} className="bg-white">
                <div data-testid={`trust-why-${i + 1}`} className="h-full p-8">
                  <Icon className="h-6 w-6 text-clay" strokeWidth={1.5} />
                  <h3 className="mt-5 font-serif text-xl tracking-tight text-ink">{w.t}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-ink/60">{w.b}</p>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={0.1}>
          <div data-testid="trust-stats" className="mt-14 grid grid-cols-2 gap-8 border-y border-ink/10 py-10 md:grid-cols-4">
            {t.trust.stats.map((s) => (
              <div key={s.l} className="text-center md:text-start">
                <p className="font-serif text-4xl text-ink md:text-5xl">{s.v}</p>
                <p className="mt-2 text-xs uppercase tracking-[0.16em] text-ink/50">{s.l}</p>
              </div>
            ))}
          </div>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 gap-6 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <div className="group h-full min-h-80 overflow-hidden bg-sand">
              <img
                src={IMGS.factory}
                alt="HHB Clothing Co factory floor"
                data-testid="trust-factory-image"
                loading="lazy"
                className="h-full w-full object-cover grayscale-[30%] transition-[transform,filter] duration-700 ease-out group-hover:scale-105 group-hover:grayscale-0"
              />
            </div>
          </Reveal>
          <div className="grid grid-cols-1 gap-6 lg:col-span-7">
            {t.trust.quotes.map((q, i) => (
              <Reveal key={q.name} delay={i * 0.08}>
                <blockquote
                  data-testid={`testimonial-card-${i + 1}`}
                  className="flex flex-col justify-between gap-4 border border-ink/10 bg-bone p-8 transition-shadow duration-500 hover:shadow-[0_12px_40px_rgba(0,0,0,0.05)] sm:flex-row sm:items-center"
                >
                  <p className="max-w-xl font-serif text-lg italic leading-relaxed text-ink/75">“{q.text}”</p>
                  <footer className="shrink-0 sm:text-end">
                    <p className="text-sm font-semibold text-ink">{q.name}</p>
                    <p className="mt-1 text-xs uppercase tracking-[0.14em] text-ink/50">{q.role}</p>
                  </footer>
                </blockquote>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Trust;
